import { clsx, type ClassValue } from 'clsx';
import * as Color from 'color-bits';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Helper function to convert any CSS color to rgba
export const getRGBA = (
  cssColor: React.CSSProperties['color'],
  fallback: string = 'rgba(180, 180, 180)',
): string => {
  if (typeof window === 'undefined') return fallback;
  if (!cssColor) return fallback;

  try {
    // Handle CSS variables
    if (typeof cssColor === 'string' && cssColor.startsWith('var(')) {
      const element = document.createElement('div');
      element.style.color = cssColor;
      document.body.appendChild(element);
      const computedColor = window.getComputedStyle(element).color;
      document.body.removeChild(element);
      return Color.formatRGBA(Color.parse(computedColor));
    }

    return Color.formatRGBA(Color.parse(cssColor));
  } catch (e) {
    console.error('Color parsing failed:', e);
    return fallback;
  }
};

// Helper function to add opacity to an RGB color string
export const colorWithOpacity = (color: string, opacity: number): string => {
  if (!color.startsWith('rgb')) return color;
  return Color.formatRGBA(Color.alpha(Color.parse(color), opacity));
};

// Tremor Raw focusInput [v0.0.1]

export const focusInput = [
  // base
  'focus:ring-2',
  // ring color
  'focus:ring-blue-200 focus:dark:ring-blue-700/30',
  // border color
  'focus:border-blue-500 focus:dark:border-blue-700',
];

// Tremor Raw focusRing [v0.0.1]

export const focusRing = [
  // base
  'outline outline-offset-2 outline-0 focus-visible:outline-2',
  // outline color
  'outline-blue-500 dark:outline-blue-500',
];

// Tremor Raw hasErrorInput [v0.0.1]

export const hasErrorInput = [
  // base
  'ring-2',
  // border color
  'border-red-500 dark:border-red-700',
  // ring color
  'ring-red-200 dark:ring-red-700/30',
];

export function truncateString(str?: string, maxLength = 50) {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + '...';
}

/**
 * Icon name mappings for special cases where Lucide uses different naming conventions
 * Maps kebab-case names to their actual Lucide icon component names
 */
const ICON_NAME_MAPPINGS: Record<string, string> = {
  'wand-2': 'Wand2',
  'message-circle': 'MessageCircle',
  'message-square': 'MessageSquare',
  'message-square-more': 'MessageSquareMore',
  'chevron-left': 'ChevronLeft',
  'chevron-right': 'ChevronRight',
  'chevron-up': 'ChevronUp',
  'chevron-down': 'ChevronDown',
  'arrow-left': 'ArrowLeft',
  'arrow-right': 'ArrowRight',
  'arrow-up': 'ArrowUp',
  'arrow-down': 'ArrowDown',
};

/**
 * Converts kebab-case or lowercase icon names to PascalCase for Lucide DynamicIcon
 * Uses special mappings for icons that don't follow standard PascalCase conversion
 * @example toPascalCase('message-circle') => 'MessageCircle'
 * @example toPascalCase('bot') => 'Bot'
 * @example toPascalCase('MessageCircle') => 'MessageCircle' (already PascalCase)
 * @example toPascalCase('wand-2') => 'Wand2'
 */
export function toPascalCase(str: string | null | undefined): string {
  // Handle null/undefined/empty
  if (!str || typeof str !== 'string' || !str.trim()) {
    return 'Bot'; // Default fallback icon
  }
  
  const trimmed = str.trim();
  
  // Check special mappings first
  const lowerTrimmed = trimmed.toLowerCase();
  if (ICON_NAME_MAPPINGS[lowerTrimmed]) {
    return ICON_NAME_MAPPINGS[lowerTrimmed];
  }
  
  // Check if already PascalCase (starts with uppercase letter, no hyphens)
  if (/^[A-Z]/.test(trimmed) && !trimmed.includes('-') && !trimmed.includes('_')) {
    return trimmed;
  }
  
  // Convert kebab-case or lowercase to PascalCase
  const pascalCase = trimmed
    .split(/[-_]/) // Split on hyphens or underscores
    .filter(part => part.length > 0) // Remove empty parts
    .map((word, index) => {
      // Handle numbers (like 'wand-2' -> 'Wand2')
      if (/^\d+$/.test(word)) {
        return word;
      }
      // First word: capitalize first letter
      // Subsequent words: capitalize first letter
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
  
  // Ensure we have at least one character
  if (!pascalCase) {
    return 'Bot';
  }
  
  return pascalCase;
}

/**
 * Validates and normalizes icon name for Lucide DynamicIcon with existence check
 * This function should be used at runtime to ensure the icon exists
 */
export function getValidIconName(
  iconName: string | null | undefined,
  availableIcons?: Record<string, any>
): string {
  const pascalCaseName = toPascalCase(iconName);
  
  // If we have access to available icons, validate against them
  if (availableIcons && !availableIcons[pascalCaseName]) {
    // Try common alternatives if the icon doesn't exist
    const fallbacks = ['Bot', 'Sparkles', 'Zap', 'Circle'];
    for (const fallback of fallbacks) {
      if (availableIcons[fallback]) {
        return fallback;
      }
    }
    // Default to Bot if nothing else works
    return 'Bot';
  }
  
  return pascalCaseName;
}


